import React, { useState, useEffect, useCallback } from 'react';
import {
    Table,
    Card,
    Button,
    Space,
    Modal,
    Form,
    Select,
    message,
    Tag,
    Popconfirm,
    InputNumber,
    Input,
    Typography,
    Alert,
    Tooltip,
} from 'antd';
import {
    PlusOutlined,
    DeleteOutlined,
    ReloadOutlined,
    InfoCircleOutlined,
    ThunderboltOutlined,
} from '@ant-design/icons';
import evmAdminService from '../../../shared/services/evmAdminService';

const { Option } = Select;
const { Title, Text } = Typography;

// 与后端保持一致的数组解析(兼容 paginator / 裸数组 / 包裹对象)
const extractArray = (data, ...keys) => {
    if (Array.isArray(data)) return data;
    for (const key of keys) {
        if (Array.isArray(data?.[key])) return data[key];
    }
    if (Array.isArray(data?.data)) return data.data;
    return [];
};

const statusMap = {
    active: { color: 'success', text: '活跃' },
    pending: { color: 'processing', text: '待生效' },
    error: { color: 'error', text: '异常' },
    deleting: { color: 'warning', text: '删除中' },
};

const PortForwardManagement = () => {
    const [loading, setLoading] = useState(false);
    const [forwards, setForwards] = useState([]);
    const [users, setUsers] = useState([]);
    const [vms, setVms] = useState([]);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
    const [filterVm, setFilterVm] = useState(null);
    const [filterStatus, setFilterStatus] = useState(null);
    const [modalVisible, setModalVisible] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [form] = Form.useForm();

    const fetchForwards = useCallback(async () => {
        setLoading(true);
        try {
            const params = {
                page: pagination.current,
                per_page: pagination.pageSize,
            };
            if (filterVm) params.vm_id = filterVm;
            if (filterStatus) params.status = filterStatus;
            const response = await evmAdminService.getPortForwardList(params);
            if (response.status === 'success') {
                setForwards(extractArray(response.data));
                setPagination(prev => ({ ...prev, total: response.data?.total || 0 }));
            } else {
                message.error(response.msg || '加载端口转发规则失败');
            }
        } catch (error) {
            message.error('加载端口转发规则失败');
        }
        setLoading(false);
    }, [pagination.current, pagination.pageSize, filterVm, filterStatus]);

    const fetchMeta = useCallback(async () => {
        try {
            const [usersRes, vmsRes] = await Promise.all([
                evmAdminService.getUsers(),
                evmAdminService.getVms({ per_page: 500 }),
            ]);
            if (usersRes.status === 'success') setUsers(extractArray(usersRes.data, 'users'));
            if (vmsRes.status === 'success') setVms(extractArray(vmsRes.data, 'vms'));
        } catch (error) {
            // 元数据加载失败不阻塞主列表
        }
    }, []);

    useEffect(() => {
        fetchForwards();
    }, [fetchForwards]);

    useEffect(() => {
        fetchMeta();
    }, [fetchMeta]);

    const userLabel = (userId) => {
        if (!userId) return '-';
        const u = users.find(user => user.id === userId);
        return u ? `${u.username} (ID: ${u.id})` : `用户 ${userId}`;
    };

    const handleCreate = () => {
        form.resetFields();
        setSelectedUserId(null);
        form.setFieldsValue({ protocol: 'tcp' });
        setModalVisible(true);
    };

    const handleDelete = async (id) => {
        try {
            const response = await evmAdminService.deletePortForward(id);
            if (response.status === 'success') {
                message.success('端口转发已删除');
                fetchForwards();
            } else {
                message.error(response.msg || '删除失败');
            }
        } catch (error) {
            message.error('删除失败');
        }
    };

    const handleForceDelete = async (id) => {
        try {
            const response = await evmAdminService.forceDeletePortForward(id);
            if (response.status === 'success') {
                message.success('端口转发已强制删除');
                fetchForwards();
            } else {
                message.error(response.msg || '强制删除失败');
            }
        } catch (error) {
            message.error('强制删除失败');
        }
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setSubmitting(true);
            const payload = {
                vm_id: values.vm_id,
                protocol: values.protocol,
                external_port: values.external_port,
                internal_port: values.internal_port,
            };
            if (values.internal_ip) payload.internal_ip = values.internal_ip;
            if (values.description) payload.description = values.description;
            const response = await evmAdminService.createPortForward(payload);
            if (response.status === 'success') {
                message.success('端口转发规则已创建');
                setModalVisible(false);
                fetchForwards();
            } else {
                message.error(response.msg || '创建失败');
            }
        } catch (error) {
            if (!error.errorFields) {
                message.error('创建失败');
            }
        } finally {
            setSubmitting(false);
        }
    };

    const vmDisplay = (record) => {
        const vm = record.vm || vms.find(v => v.id === record.vm_id);
        if (!vm) return `VM ${record.vm_id}`;
        return (
            <Space direction="vertical" size={0}>
                <span>{vm.hostname || `VM-${record.vm_id}`}</span>
                <Text type="secondary" style={{ fontSize: 12 }}>{vm.ip_address || vm.main_ip || '-'}</Text>
            </Space>
        );
    };

    const filteredVms = selectedUserId ? vms.filter(vm => vm.uid === selectedUserId) : vms;

    const columns = [
        { title: 'ID', dataIndex: 'id', width: 60 },
        {
            title: '用户',
            width: 160,
            render: (_, record) => userLabel(record.vm?.user_id || record.user_id),
        },
        {
            title: '虚拟机',
            width: 180,
            render: (_, record) => vmDisplay(record),
        },
        {
            title: '协议',
            dataIndex: 'protocol',
            width: 80,
            render: (protocol) => (
                <Tag color={protocol === 'tcp' ? 'blue' : 'green'}>{protocol?.toUpperCase()}</Tag>
            ),
        },
        {
            title: '外部',
            width: 170,
            render: (_, record) => (
                <Space direction="vertical" size={0}>
                    <span>{record.external_ip || record.node?.public_ip || '*'}:{record.external_port}</span>
                    <Text type="secondary" style={{ fontSize: 12 }}>公网</Text>
                </Space>
            ),
        },
        {
            title: '内部',
            width: 170,
            render: (_, record) => (
                <Space direction="vertical" size={0}>
                    <span>{record.internal_ip}:{record.internal_port}</span>
                    <Text type="secondary" style={{ fontSize: 12 }}>内网</Text>
                </Space>
            ),
        },
        {
            title: '节点',
            width: 120,
            render: (_, record) => record.node?.name || record.node?.hostname || (record.node_id ? `节点 ${record.node_id}` : '-'),
        },
        {
            title: '状态',
            dataIndex: 'status',
            width: 100,
            render: (status) => {
                const info = statusMap[status] || { color: 'default', text: status || '活跃' };
                return <Tag color={info.color}>{info.text}</Tag>;
            },
        },
        {
            title: '创建时间',
            dataIndex: 'created_at',
            width: 160,
            render: (date) => (date ? new Date(date).toLocaleString() : '-'),
        },
        {
            title: '操作',
            width: 120,
            fixed: 'right',
            render: (_, record) => (
                <Space>
                    <Popconfirm
                        title="删除此端口转发？"
                        description={`将停止把流量转发到 ${record.internal_ip}:${record.internal_port}，相关入站连接会被中断。`}
                        onConfirm={() => handleDelete(record.id)}
                        okText="删除"
                        okType="danger"
                        cancelText="取消"
                    >
                        <Tooltip title="删除">
                            <Button type="link" danger icon={<DeleteOutlined />} />
                        </Tooltip>
                    </Popconfirm>
                    <Popconfirm
                        title="强制删除此端口转发？"
                        description="强制删除会直接清除数据库记录(用于清理残留/异常规则),不会等待节点确认,请谨慎操作。"
                        onConfirm={() => handleForceDelete(record.id)}
                        okText="强制删除"
                        okType="danger"
                        cancelText="取消"
                    >
                        <Tooltip title="强制删除(清理残留/异常规则)">
                            <Button type="link" danger icon={<ThunderboltOutlined />} />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div>
            <Card>
                <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                    <Title level={4} style={{ margin: 0 }}>端口转发管理</Title>
                    <Space wrap>
                        <Select
                            allowClear
                            showSearch
                            placeholder="按虚拟机筛选"
                            style={{ width: 220 }}
                            value={filterVm}
                            optionFilterProp="children"
                            onChange={(value) => { setFilterVm(value || null); setPagination(prev => ({ ...prev, current: 1 })); }}
                        >
                            {vms.map(vm => (
                                <Option key={vm.id} value={vm.id}>
                                    {vm.hostname || `VM-${vm.id}`} (ID: {vm.id})
                                </Option>
                            ))}
                        </Select>
                        <Select
                            allowClear
                            placeholder="按状态筛选"
                            style={{ width: 140 }}
                            value={filterStatus}
                            onChange={(value) => { setFilterStatus(value || null); setPagination(prev => ({ ...prev, current: 1 })); }}
                        >
                            {Object.keys(statusMap).map(key => (
                                <Option key={key} value={key}>{statusMap[key].text}</Option>
                            ))}
                        </Select>
                        <Button icon={<ReloadOutlined />} onClick={fetchForwards}>刷新</Button>
                        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
                            添加规则
                        </Button>
                    </Space>
                </div>

                <Alert
                    message="端口转发说明"
                    description="端口转发允许外部流量通过 NAT 访问虚拟机的内部服务,适用于仅有内网 IP 的虚拟机。此处可管理全部用户创建的转发规则,强制删除用于清理节点侧已不存在的残留/异常规则。"
                    type="info"
                    showIcon
                    icon={<InfoCircleOutlined />}
                    style={{ marginBottom: 16 }}
                />

                <Table
                    columns={columns}
                    dataSource={forwards}
                    rowKey="id"
                    loading={loading}
                    scroll={{ x: 1200 }}
                    pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: pagination.total,
                        showSizeChanger: true,
                        showTotal: (total) => `共 ${total} 条规则`,
                        onChange: (page, pageSize) => setPagination(prev => ({ ...prev, current: page, pageSize })),
                    }}
                />
            </Card>

            <Modal
                title="创建端口转发规则"
                open={modalVisible}
                onOk={handleSubmit}
                onCancel={() => setModalVisible(false)}
                okText="创建"
                cancelText="取消"
                okButtonProps={{ loading: submitting }}
                width={600}
            >
                <Form form={form} layout="vertical">
                    <Form.Item label="所属用户(用于过滤虚拟机)">
                        <Select
                            allowClear
                            showSearch
                            placeholder="选择用户后仅显示其虚拟机"
                            optionFilterProp="children"
                            value={selectedUserId}
                            onChange={(value) => {
                                setSelectedUserId(value || null);
                                form.setFieldsValue({ vm_id: undefined });
                            }}
                        >
                            {users.map(user => (
                                <Option key={user.id} value={user.id}>
                                    {user.username} (ID: {user.id})
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="vm_id"
                        label="目标虚拟机"
                        rules={[{ required: true, message: '请选择虚拟机' }]}
                    >
                        <Select placeholder="选择虚拟机" optionFilterProp="children" showSearch>
                            {filteredVms.map(vm => (
                                <Option key={vm.id} value={vm.id}>
                                    {vm.hostname || `VM-${vm.id}`} ({vm.ip_address || vm.main_ip || '无 IP'})
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="protocol"
                        label="协议"
                        rules={[{ required: true, message: '请选择协议' }]}
                    >
                        <Select>
                            <Option value="tcp">TCP</Option>
                            <Option value="udp">UDP</Option>
                        </Select>
                    </Form.Item>

                    <Space style={{ width: '100%' }} size="large">
                        <Form.Item
                            name="external_port"
                            label="外部端口"
                            rules={[
                                { required: true, message: '请输入外部端口' },
                                { type: 'number', min: 1, max: 65535, message: '端口必须在 1-65535 之间' },
                            ]}
                            style={{ flex: 1 }}
                        >
                            <InputNumber min={1} max={65535} style={{ width: '100%' }} placeholder="例如 8080" />
                        </Form.Item>

                        <Form.Item
                            name="internal_port"
                            label="内部端口"
                            rules={[
                                { required: true, message: '请输入内部端口' },
                                { type: 'number', min: 1, max: 65535, message: '端口必须在 1-65535 之间' },
                            ]}
                            style={{ flex: 1 }}
                        >
                            <InputNumber min={1} max={65535} style={{ width: '100%' }} placeholder="例如 80" />
                        </Form.Item>
                    </Space>

                    <Form.Item
                        name="internal_ip"
                        label="内部 IP(可选)"
                        extra="留空则自动使用虚拟机主 IP"
                    >
                        <Input placeholder="例如 10.0.0.5" />
                    </Form.Item>

                    <Form.Item name="description" label="备注(可选)">
                        <Input placeholder="用途说明" />
                    </Form.Item>

                    <Alert
                        message="工作原理"
                        description={
                            <ul style={{ margin: 0, paddingLeft: 20 }}>
                                <li>访问 <code>公网IP:外部端口</code> 的流量会转发到 <code>虚拟机内网IP:内部端口</code></li>
                                <li>请确认目标端口未被防火墙或安全组阻止</li>
                                <li>常用端口:22 (SSH)、80 (HTTP)、443 (HTTPS)、3306 (MySQL)</li>
                            </ul>
                        }
                        type="warning"
                        style={{ marginTop: 16 }}
                    />
                </Form>
            </Modal>
        </div>
    );
};

export default PortForwardManagement;
